// src/components/course/CourseCard.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Globe, Layers3 } from 'lucide-react';
import {
  COURSE_COVER_FALLBACK,
  AVATAR_FALLBACK,
  setFallback,
} from '../../lib/placeholders';
import { computePricing, formatKzt } from '../../lib/coursePricing';

const CourseCard = ({ course }) => {
  const navigate = useNavigate();

  const getFirstCategory = (categories) => {
    if (Array.isArray(categories)) return categories?.[0]?.title || 'Без категории';
    return categories?.title || 'Без категории';
  };

  const handleCourseClick = (e) => {
    e.preventDefault();
    navigate(`/course/${course._id}`);
  };

  const handleInstructorClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (course.instructor?._id) {
      navigate(`/instructor/${course.instructor._id}`);
    }
  };

  // Все цены через единый helper. Раньше тут была своя логика основанная
  // на discountPrice — она ломалась на курсах с discount=20% но discountPrice=null.
  const { basePrice, finalPrice, discountPercent, hasDiscount } = computePricing(course);

  const coverFallback = COURSE_COVER_FALLBACK;
  const avatarFallback = AVATAR_FALLBACK;

  return (
      <div className="group flex h-full w-full flex-col overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-700/70 bg-white dark:bg-gray-900 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
        {/* Cover — фиксированный 16:9 контейнер + полная картинка через
            object-contain. Раньше object-cover обрезал изображение по краям
            (плохо для портретных/квадратных обложек). Теперь:
              • контейнер aspect-video → все карточки одной высоты
              • backdrop: та же картинка, увеличена и размыта → заполняет
                "пустые" поля без бесплатных рамок (паттерн YouTube/Coursera)
              • foreground: object-contain → картинка видна ЦЕЛИКОМ
            Финал: каталог визуально ровный, ни одна обложка не обрезается. */}
        <div
            className="relative w-full aspect-video overflow-hidden cursor-pointer bg-gray-100 dark:bg-gray-800"
            onClick={handleCourseClick}
        >
          {course.coverImageUrl ? (
              <>
                {/* Backdrop — размытая копия для красивого заполнения полей. */}
                <img
                    src={course.coverImageUrl}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-50 dark:opacity-30"
                />
                {/* Foreground — целая картинка, не обрезается. */}
                <img
                    src={course.coverImageUrl}
                    alt={course.title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    onError={setFallback(coverFallback)}
                />
              </>
          ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600">
            <span className="text-2xl font-bold text-white">
              {course.title?.substring(0, 2)?.toUpperCase() || 'CR'}
            </span>
              </div>
          )}

          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/35 to-transparent" />

          <div className="absolute left-4 top-4 inline-flex items-center rounded-full bg-white/95 dark:bg-gray-900/90 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 shadow-sm backdrop-blur">
            {getFirstCategory(course.category)}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col p-5">
          {/* Title */}
          <div className="mb-2">
            <h3
                className="line-clamp-2 cursor-pointer text-lg font-semibold leading-7 text-gray-900 dark:text-white break-words"
                onClick={handleCourseClick}
            >
              {course.title}
            </h3>
          </div>

          {/* Description — break-words/overflow-wrap защищают от длинных URL без пробелов */}
          <div className="mb-4 min-h-[60px]">
            <p className="line-clamp-3 text-sm leading-6 text-gray-500 dark:text-gray-400 break-words [overflow-wrap:anywhere]">
              {course.description || 'Описание курса будет добавлено позже.'}
            </p>
          </div>

          {/* Meta */}
          <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <div className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5">
              <Globe className="h-3.5 w-3.5" />
              {course.language || 'Русский'}
            </div>

            <div className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5">
              <Layers3 className="h-3.5 w-3.5" />
              {course.sectionsCount ?? course.sections?.length ?? 0} разделов
            </div>

            {course.lessonsCount !== undefined && (
                <div className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5">
                  <BookOpen className="h-3.5 w-3.5" />
                  {course.lessonsCount} уроков
                </div>
            )}
          </div>

          {/* Price */}
          <div className="mb-5">
            {hasDiscount ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-400 line-through tabular-nums">
                    {formatKzt(basePrice)}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-400 tabular-nums">
                    {formatKzt(finalPrice)}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-rose-100 dark:bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:text-rose-300">
                    −{discountPercent}%
                  </span>
                </div>
            ) : (
                <span className="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-950/30 px-3 py-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 tabular-nums">
                  {formatKzt(basePrice)}
                </span>
            )}
          </div>

          {/* Instructor */}
          <button
              type="button"
              onClick={handleInstructorClick}
              className="mb-4 flex items-center gap-3 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/70 p-3 text-left transition hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <img
                src={course.instructor?.avatar || course.instructor?.avatarUrl || avatarFallback}
                alt={course.instructorProfile?.publicName || course.instructor?.name || 'Инструктор'}
                loading="lazy"
                className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-gray-700"
                onError={setFallback(avatarFallback)}
            />

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                {course.instructorProfile?.publicName ||
                    course.instructor?.name ||
                    'Инструктор не указан'}
              </p>
              <p className="truncate text-xs text-indigo-600 dark:text-indigo-400">
                Открыть профиль →
              </p>
            </div>
          </button>

          {/* Button */}
          <button
              type="button"
              onClick={handleCourseClick}
              className="mt-auto inline-flex w-full items-center justify-center rounded-2xl bg-gray-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
          >
            Подробнее о курсе
          </button>
        </div>
      </div>
  );
};

export default CourseCard;